import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * AdminLayout — sidebar shell for every Nexus admin page.
 *
 * Rendered as the layout-route element in routes.tsx so each nested
 * Route renders into the <Outlet /> on the right while the sidebar
 * persists across navigation.
 *
 *   ┌──────────────┬────────────────────────────────────────────┐
 *   │              │                                            │
 *   │  Nexus       │   <Outlet />  (page content)               │
 *   │  sidebar     │                                            │
 *   │              │                                            │
 *   └──────────────┴────────────────────────────────────────────┘
 *
 * Bootstrap 5 utility classes only — no external CSS file. Host SPAs
 * already pull in Bootstrap (AquaStar-Admin imports it at boot), so the
 * package stays zero-asset.
 *
 * The `basePath` prop comes from <NexusAdminRoutes basePath=...> and is
 * prepended to every NavLink so the sidebar reflects the parent SPA's
 * actual URL (e.g. '/platform/broker' not '/broker').
 *
 * Optional `backHref` + `backLabel` render a "← Back to <App>" link
 * above the sidebar nav. The platform admin pages live INSIDE a host
 * SPA — operators need a one-click exit back to the host's own admin
 * home so they're not stranded in the Nexus shell. Both default to
 * absent (no back link rendered) so SPAs that don't pass them get
 * the prior behaviour unchanged.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useNexusFetch } from '../api.js';
import { ProfileMenu } from './ProfileMenu.js';
import { ApprovalsBell } from './ApprovalsBell.js';
import { useJoinRequestCount } from '../pages/joinRequests.js';
import { ConsoleNavContext } from './consoleNav.js';
// Sidebar sections (Erwin, 2026-09-22): like items together instead of one
// flat list in the order pages were added. Health stays first (landing page).
// A section whose every item is capability-hidden disappears with them.
// Sections are collapsible and collapsed by default except Overview; the
// section holding the current page is always open, and the viewer's own
// toggles are remembered in localStorage (per browser — a convenience only).
/** A client instance on the Gateway: ai_gateway on, fleet_control off (NEXUS-1386). */
const CLIENT_GATEWAY = { on: ['ai_gateway'], off: ['fleet_control'] };
const NAV = [
    {
        label: 'Overview',
        items: [
            { to: '', label: 'Health', hint: 'Service health dashboard' },
            { to: 'system-map', label: 'System map', hint: 'Live architecture diagram — runtime topology and deployed services' },
            { to: 'platform-health', label: 'Platform health', hint: 'health monitor checks + gated-recovery config (monitor + 4h agent)' },
        ],
    },
    {
        label: 'Apps & work',
        items: [
            { to: 'apps', label: 'Apps', hint: 'Registered apps + manifests' },
            { to: 'capabilities', label: 'Capabilities', hint: 'Per-app feature access — the apps \u00d7 capabilities matrix' },
            { to: 'users', label: 'Users', hint: 'Platform-wide user list — every account on this Nexus instance' },
            { to: 'join-requests', label: 'Join requests', hint: 'Self-signups waiting for approval on apps that require it — approve, deny, or link to an existing member', badge: 'joinRequests' },
            { to: 'issues', label: 'Issues', hint: 'Issue tracker — cross-app backlog, per-app config + notifications' },
            { to: 'roadmap', label: 'Roadmap', hint: 'Client-facing period plan — approve items, follow the hours', requires: 'roadmap' },
            { to: 'wiki', label: 'Wiki', hint: 'Docs-as-code wiki — Markdown synced from git, searchable, linked to issues', requires: 'wiki' },
        ],
    },
    {
        // NEXUS-1356 (Erwin, 2026-10-26): everything AI in one place — it used to be
        // spread over Apps & work › Skills, Services › AI hosts and Services › Rate cards.
        label: 'AI',
        items: [
            // NEXUS-1386: a client instance with AI › Gateway (ai_gateway on, fleet_control
            // off) never sees the fleet pages. With ai_gateway off nothing changes (ADR-0025).
            { to: 'ai/gateway', label: 'Gateway', hint: 'Which AI service answers for this instance — Nexus AI, OpenAI, Anthropic, Gemini, AWS Bedrock', requires: 'ai_gateway', strict: true },
            { to: 'ai/service', label: 'Service', hint: 'Who serves this instance\'s AI — manage the fleet here or follow another instance; per-app resolution, fallback paths, execution policy, image + voice services', hideWhen: CLIENT_GATEWAY },
            { to: 'ai/hosts', label: 'Hosts & models', hint: 'AI-capable machines — Ollama and Cloud-CLI hosts, installed models, pulls, worker recovery', hideWhen: CLIENT_GATEWAY },
            { to: 'ai/providers', label: 'Providers', hint: 'Chat provider rows — which host + model a name routes to, labels, the instance default', hideWhen: CLIENT_GATEWAY },
            { to: 'ai/agents', label: 'Agents', hint: 'Agent runners and fleet agents — the headless workers that run agent_control jobs', hideWhen: CLIENT_GATEWAY },
            { to: 'skills', label: 'Skills', hint: 'Agent skills — versions, diffs, bindings, usage, kill switch (ADR-0024)', requires: 'agent_skills' },
            { to: 'usage', label: 'Usage', hint: 'Every metered service per client and invoice period — AI turns with chain, model, context, tokens and cost; subscription lanes; electricity (NEXUS-1350)' },
            { to: 'rate-cards', label: 'Pricing', hint: 'Rate cards — what a unit of each service costs a client (per kind, model, provider kind, app)' },
        ],
    },
    {
        label: 'Platform',
        items: [
            { to: 'hosts', label: 'Hosts', hint: 'Lab PCs — CPU/GPU temperatures, fan speeds, power (NEXUS-102)' },
            { to: 'power', label: 'Power', hint: 'Wall power per host via smart plugs — watts, kWh, electricity cost (NEXUS-1354)' },
            { to: 'remote-access', label: 'Remote access', hint: 'Browser shell to lab hosts with keychain password paste (NEXUS-535)' },
            { to: 'repo-guard', label: 'Repo guard', hint: 'Flags pushes to develop/uat/main that did not come through an approved PR (NEXUS-601)' },
            { to: 'databases', label: 'Databases', hint: 'Platform DB + per-app DBs + backups + live queries/locks' },
            { to: 'retention', label: 'Retention', hint: 'How long snapshots, backups and logs are kept — rules the pruning jobs follow' },
            { to: 'file-storage', label: 'File storage', hint: 'File service backend (local FS ↔ S3) + per-app usage' },
            { to: 'media-library', label: 'Media library', hint: 'Own photo/video library, or show another instance\'s read-only (dev/uat → prod)' },
            { to: 'scheduled-tasks', label: 'Scheduled tasks', hint: 'systemd timers reconciled from scheduled_tasks table' },
            { to: 'keychain', label: 'Keychain', hint: 'Your own password vault — works with the Bitwarden apps (NEXUS-569)' },
            { to: 'credentials', label: 'Credentials', hint: 'Encrypted credentials store — provider API keys, broker secrets' },
            { to: 'access-tokens', label: 'Access tokens', hint: 'Personal access tokens for scripts and tools that act as you' },
            { to: 'audit-log', label: 'Audit log', hint: 'Admin-action audit trail' },
        ],
    },
    {
        label: 'Services',
        items: [
            { to: 'broker', label: 'Broker creds', hint: 'MQTT broker credentials' },
            { to: 'translator', label: 'Translator', hint: 'Device translator status' },
            { to: 'email', label: 'Email', hint: 'SMTP test send + recent outbound mail history' },
            { to: 'collab', label: 'Collab', hint: 'Rooms & Jitsi Meet sessions' },
        ],
    },
    {
        label: 'Integrations',
        items: [
            { to: 'plaid', label: 'Plaid', hint: 'Plaid integration — config, linked bank items, webhook health', requires: 'plaid' },
            { to: 'places', label: 'Places', hint: 'Places search service — API key, test connection, global limits', requires: 'places' },
        ],
    },
];
const OPEN_KEY = 'nexus-admin.nav.open';
const DEFAULT_OPEN = ['Overview'];
function readOpen() {
    try {
        const raw = window.localStorage.getItem(OPEN_KEY);
        if (raw) {
            const v = JSON.parse(raw);
            if (Array.isArray(v))
                return v.filter((x) => typeof x === 'string');
        }
    }
    catch { /* private mode / blocked storage → defaults */ }
    return DEFAULT_OPEN;
}
/** Group label that holds `pathname` (the longest matching item wins, so '' = Health only matches the base). */
export function groupForPath(pathname, base) {
    if (pathname !== base && !pathname.startsWith(`${base}/`))
        return null;
    const rel = pathname.slice(base.length).replace(/^\/+/, '');
    let best = null;
    for (const g of NAV) {
        for (const item of g.items) {
            const hit = item.to === '' ? rel === '' : rel === item.to || rel.startsWith(`${item.to}/`);
            if (hit && (!best || item.to.length > best.len))
                best = { label: g.label, len: item.to.length };
        }
    }
    return best?.label ?? null;
}
/**
 * Whether a nav item renders for the instance capability map (`null` = not
 * loaded yet). Fail-open for ordinary gated items: they hide only when the
 * capability is explicitly false. `strict` items show only when explicitly true.
 */
export function navItemVisible(item, caps) {
    if (item.hideWhen && caps !== null
        && item.hideWhen.on.every((k) => caps[k] === true)
        && item.hideWhen.off.every((k) => caps[k] !== true))
        return false;
    if (!item.requires)
        return true;
    if (item.strict)
        return caps !== null && caps[item.requires] === true;
    if (caps === null)
        return true; // still loading → show
    return caps[item.requires] !== false; // hide only when explicitly false
}
/** The sidebar for a capability map; a section whose items are all hidden disappears. */
export function visibleNav(caps) {
    return NAV.map((g) => ({ ...g, items: g.items.filter((i) => navItemVisible(i, caps)) })).filter((g) => g.items.length > 0);
}
export function AdminLayout({ basePath, backHref, backLabel }) {
    const trimmedBase = basePath.replace(/\/+$/, '');
    // Capability flags drive which gated nav items render. `null` = not loaded
    // yet → show everything (no flicker-hide of enabled items). Once loaded, a
    // gated item hides only when its capability is explicitly false.
    const navigate = useNavigate();
    const fetcher = useNexusFetch();
    // Pages link to each other through this (see consoleNav.ts).
    const consoleNav = useMemo(() => ({ basePath: trimmedBase, navigate: (p) => navigate(p) }), [trimmedBase, navigate]);
    const [caps, setCaps] = useState(null);
    useEffect(() => {
        let alive = true;
        fetcher('/v1/admin/capabilities')
            .then((r) => {
            if (alive)
                setCaps(r?.capabilities ?? {});
        })
            .catch(() => {
            // Fail-open: an older gateway without the endpoint, or a transient
            // error, leaves all items visible rather than hiding real features.
            if (alive)
                setCaps({});
        });
        return () => {
            alive = false;
        };
    }, [fetcher]);
    const navGroups = visibleNav(caps);
    // Join requests waiting for a decision (polled; see useJoinRequestCount).
    const joinRequests = useJoinRequestCount(fetcher);
    const badgeFor = (badge) => (badge === 'joinRequests' ? joinRequests ?? 0 : 0);
    const location = useLocation();
    // Small screens (< lg, 992 px): the sidebar is an off-canvas drawer behind a
    // hamburger in the top bar — the industry pattern for admin consoles on phones.
    // It closes on navigation, on the backdrop, and on Escape; wide screens never
    // see it (the sidebar is fixed there, unchanged). Erwin 2026-10-26: on iOS
    // Chrome the fixed sidebar took half the screen in portrait.
    const [drawer, setDrawer] = useState(false);
    useEffect(() => { setDrawer(false); }, [location.pathname]);
    useEffect(() => {
        if (!drawer || typeof window === 'undefined')
            return;
        const onKey = (e) => { if (e.key === 'Escape')
            setDrawer(false); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [drawer]);
    const [open, setOpen] = useState(() => (typeof window === 'undefined' ? DEFAULT_OPEN : readOpen()));
    const activeGroup = groupForPath(location.pathname, trimmedBase);
    const isOpen = (label) => open.includes(label) || label === activeGroup;
    const toggle = (label) => {
        setOpen((prev) => {
            // Collapsing the active section is allowed for the moment (the page stays
            // where it is), so a toggle on it removes it from the remembered set even
            // though it renders open until the user navigates elsewhere.
            const currentlyOpen = prev.includes(label) || label === activeGroup;
            const next = currentlyOpen ? prev.filter((l) => l !== label) : [...prev, label];
            try {
                window.localStorage.setItem(OPEN_KEY, JSON.stringify(next));
            }
            catch { /* ignore */ }
            return next;
        });
    };
    return (_jsxs("div", { className: "d-flex", style: { minHeight: '100vh' }, children: [_jsx("style", { children: `
        .nexus-admin-aside { width: 240px; flex: 0 0 240px; }
        .nexus-admin-backdrop { display: none; }
        @media (max-width: 991.98px) {
          .nexus-admin-aside {
            position: fixed; top: 0; bottom: 0; left: 0; z-index: 1045;
            overflow-y: auto; transform: translateX(-100%); transition: transform 160ms ease-out;
          }
          .nexus-admin-aside.is-open { transform: none; box-shadow: 0 0 24px rgba(0,0,0,.4); }
          .nexus-admin-backdrop.is-open { display: block; position: fixed; inset: 0; z-index: 1040; background: rgba(0,0,0,.45); }
        }
      ` }), _jsx("div", { className: `nexus-admin-backdrop${drawer ? ' is-open' : ''}`, onClick: () => setDrawer(false), "aria-hidden": "true", "data-testid": "nav-backdrop" }), _jsxs("aside", { id: "nexus-admin-nav", className: `nexus-admin-aside bg-dark text-light p-3${drawer ? ' is-open' : ''}`, children: [backHref ? (_jsxs(Link, { to: backHref, className: "d-block text-light text-decoration-none small mb-3 px-2 py-1 rounded", style: { opacity: 0.85 }, title: backLabel ?? 'Back', children: ['← ', backLabel ?? 'Back'] })) : null, _jsx("div", { className: "fw-bold fs-5 mb-4", children: "Nexus" }), _jsx("nav", { className: "nav flex-column", children: navGroups.map((group, gi) => {
                            const expanded = isOpen(group.label);
                            const groupBadge = group.items.reduce((n, it) => n + badgeFor(it.badge), 0);
                            return (_jsxs("div", { className: gi === 0 ? '' : 'mt-2', children: [_jsxs("button", { type: "button", className: "btn btn-link text-light text-decoration-none text-uppercase small fw-semibold px-2 py-1 w-100 text-start d-flex justify-content-between align-items-center", style: { opacity: 0.6, letterSpacing: '0.06em', fontSize: '0.7rem' }, "aria-expanded": expanded, "aria-controls": `nav-group-${gi}`, onClick: () => toggle(group.label), children: [_jsxs("span", { children: [group.label, !expanded && groupBadge > 0 ? _jsx("span", { className: "badge rounded-pill bg-danger ms-2", title: `${groupBadge} waiting for a decision`, children: groupBadge }) : null] }), _jsx("span", { "aria-hidden": "true", style: { display: 'inline-block', transition: 'transform 120ms', transform: expanded ? 'rotate(90deg)' : 'none' }, children: "\u203A" })] }), _jsx("div", { id: `nav-group-${gi}`, hidden: !expanded, children: group.items.map((item) => {
                                            const to = item.to ? `${trimmedBase}/${item.to}` : trimmedBase || '/';
                                            return (_jsxs(NavLink, { to: to, end: item.to === '', className: ({ isActive }) => `nav-link text-light px-2 py-1 rounded ${isActive ? 'bg-secondary' : ''}`, title: item.hint, children: [item.label, badgeFor(item.badge) > 0 ? (_jsx("span", { className: "badge rounded-pill bg-danger ms-2", "data-testid": `nav-badge-${item.to}`, "aria-label": `${badgeFor(item.badge)} waiting`, children: badgeFor(item.badge) })) : null] }, item.to || 'index'));
                                        }) })] }, group.label));
                        }) })] }), _jsxs("main", { className: "flex-grow-1 d-flex flex-column bg-light", style: { minWidth: 0 }, children: [_jsxs("header", { className: "d-flex justify-content-between align-items-center px-3 px-lg-4 py-2 border-bottom bg-white", children: [_jsxs("button", { type: "button", className: "btn btn-outline-secondary btn-sm d-lg-none", "aria-label": drawer ? 'Close navigation' : 'Open navigation', "aria-controls": "nexus-admin-nav", "aria-expanded": drawer, onClick: () => setDrawer((d) => !d), "data-testid": "nav-toggle", children: [_jsx("span", { "aria-hidden": "true", style: { fontSize: '1.1rem', lineHeight: 1 }, children: "\u2630" }), _jsx("span", { className: "ms-2 fw-semibold", children: "Nexus" })] }), _jsx("span", { className: "d-none d-lg-block" }), _jsxs("span", { className: "d-flex align-items-center", children: [caps === null || caps.roadmap !== false ? _jsx(ApprovalsBell, { basePath: trimmedBase, navigate: navigate }) : null, _jsx(ProfileMenu, { basePath: trimmedBase })] })] }), _jsx("div", { className: "flex-grow-1 p-3 p-lg-4", children: _jsx(ConsoleNavContext.Provider, { value: consoleNav, children: _jsx(Outlet, {}) }) })] })] }));
}
//# sourceMappingURL=AdminLayout.js.map